import { useCallback, useRef, useState } from 'react';
import { evaluateExecutionGate, evaluateConfirmedExecution } from './queryExecutionGate';
import { AI_EXECUTION_NOT_CONFIRMED } from '../../../terminal/store/aiExecutionStore';
import { useAnalytics } from '../../../../core/analytics';
import type { DatabaseEngineId, DangerousQueryResult } from '../../../../core/engines';
import type { QueryExecuteSource, QueryExecutionOutcome } from './useQueryExecution';

export type DangerousQueryExecutionOptions = {
  includeActualPlan?: boolean;
  source?: QueryExecuteSource;
  /**
   * Optional binding check, re-run at confirm time. Supplied by the agent
   * execute-with-confirm protocol; the human paths pass nothing.
   */
  guard?: () => string | null;
};

type UseDangerousQueryGateParams = {
  engineId: DatabaseEngineId;
  doExecuteQuery: (
    queryText: string,
    options?: DangerousQueryExecutionOptions,
  ) => Promise<QueryExecutionOutcome>;
};

/**
 * Dangerous-query confirmation gate for the query editor tab.
 *
 * Owns the destructive-query confirmation dialog state and the single entry
 * point (`runQueryText`) every execution path funnels through. The decision
 * itself lives in `queryExecutionGate.ts` so it can be tested by running it;
 * it is stricter for agent-authored SQL — see that module.
 *
 * `runQueryText` resolves once the run really settles, including when it first
 * has to go through the confirmation dialog. Fire-and-forget callers (toolbar,
 * selection Execute, auto-execute-on-mount) simply ignore the promise; the
 * agent execute-with-confirm protocol awaits it, because that outcome is what
 * gets written back to the agent.
 */
export function useDangerousQueryGate({
  engineId,
  doExecuteQuery,
}: UseDangerousQueryGateParams) {
  const { track } = useAnalytics();

  // Dangerous query confirmation dialog state
  const [dangerousDialogOpen, setDangerousDialogOpen] = useState(false);
  const [pendingDangerousQuery, setPendingDangerousQuery] = useState<string | null>(null);
  const [pendingDangerousExecutionOptions, setPendingDangerousExecutionOptions] =
    useState<DangerousQueryExecutionOptions | null>(null);
  const [dangerInfo, setDangerInfo] = useState<DangerousQueryResult | null>(null);

  // Whoever is awaiting the parked query. The dialog is single-slot, so the
  // waiter is taken (not read) — a second prompt can never settle the wrong one.
  const dangerousOutcomeRef = useRef<((outcome: QueryExecutionOutcome) => void) | null>(null);
  const takeDangerousWaiter = useCallback(() => {
    const waiter = dangerousOutcomeRef.current;
    dangerousOutcomeRef.current = null;
    return waiter;
  }, []);

  // Handle confirmation of dangerous query
  const handleDangerousConfirm = useCallback(() => {
    const confirmedQuery = pendingDangerousQuery;
    const confirmedOptions = pendingDangerousExecutionOptions;
    const waiter = takeDangerousWaiter();

    // A1 (#73) — the destructive-query dialog's OUTCOME. `…_prompted` says it
    // appeared; this says what the user did about it. Without the pair there is
    // no way to tell a confirmation that is doing its job from one people have
    // learned to click through. `kind` is the fixed `DangerousQueryKind`
    // vocabulary, never the reason prose — no query text, no object names.
    track('feature:dangerous_query_resolved', {
      outcome: 'confirmed',
      kind: dangerInfo?.kind ?? 'unknown',
      severity: dangerInfo?.severity ?? 'high',
      engine: engineId,
    });
    setDangerousDialogOpen(false);
    setPendingDangerousQuery(null);
    setPendingDangerousExecutionOptions(null);
    setDangerInfo(null);

    // Re-check the binding the caller captured at prompt time. A rebind while
    // the dialog was open must refuse, never silently redirect the statement.
    const confirmed = evaluateConfirmedExecution({
      sql: confirmedQuery,
      guard: confirmedOptions?.guard,
    });
    if (confirmed.kind === 'refuse') {
      waiter?.({ success: false, errorMessage: confirmed.reason });
      return;
    }

    void doExecuteQuery(confirmed.sql, {
      includeActualPlan: confirmedOptions?.includeActualPlan,
      source: confirmedOptions?.source,
    }).then((outcome) => waiter?.(outcome));
  }, [
    pendingDangerousExecutionOptions,
    pendingDangerousQuery,
    doExecuteQuery,
    takeDangerousWaiter,
    track,
    dangerInfo,
    engineId,
  ]);

  // Handle cancellation of dangerous query
  const handleDangerousCancel = useCallback(() => {
    // The other half of the outcome pair — see `handleDangerousConfirm`.
    track('feature:dangerous_query_resolved', {
      outcome: 'cancelled',
      kind: dangerInfo?.kind ?? 'unknown',
      severity: dangerInfo?.severity ?? 'high',
      engine: engineId,
    });
    // Coded, not prose: the agent protocol maps this to "you declined", which
    // is a different outcome from "the query failed".
    takeDangerousWaiter()?.({ success: false, errorMessage: AI_EXECUTION_NOT_CONFIRMED });
    setDangerousDialogOpen(false);
    setPendingDangerousQuery(null);
    setPendingDangerousExecutionOptions(null);
    setDangerInfo(null);
  }, [takeDangerousWaiter, track, dangerInfo, engineId]);

  // Runs arbitrary SQL text through the dangerous-query gate and execution
  // pipeline. Used by both the toolbar (full editor content) and the floating
  // selection Execute button (the selected text only).
  const runQueryText = useCallback(async (
    textToExecute: string,
    options: {
      source: QueryExecuteSource;
      includeActualPlan?: boolean;
      guard?: () => string | null;
      /**
       * Ask even when the risk gate would not — the Visual view's MERGE on an
       * engine without MERGE, whose warning names what will really happen.
       */
      confirmWith?: DangerousQueryResult | null;
    },
  ): Promise<QueryExecutionOutcome> => {
    const decision = options.confirmWith && textToExecute.trim()
      ? { kind: 'confirm' as const, dangerInfo: options.confirmWith }
      : evaluateExecutionGate({
          engineId,
          sql: textToExecute,
          source: options.source,
        });

    if (decision.kind === 'nothing-to-run') {
      return { success: false, errorMessage: 'No query to execute.' };
    }

    if (decision.kind === 'confirm') {
      // The dialog is single-slot: a newer query replaces the older one, so
      // release anyone still waiting on the query being displaced.
      takeDangerousWaiter()?.({ success: false, errorMessage: 'Superseded by another query.' });
      setPendingDangerousQuery(textToExecute);
      setPendingDangerousExecutionOptions({
        source: options.source,
        includeActualPlan: options.includeActualPlan,
        guard: options.guard,
      });
      setDangerInfo(decision.dangerInfo);
      setDangerousDialogOpen(true);
      // The "shown" half of the pair (see `handleDangerousConfirm`). `source`
      // is the fixed `QueryExecuteSource` vocabulary — it separates a script
      // that arrived from Schema Compare's Open & Execute from SQL the user
      // typed, which is the difference between the two populations.
      track('feature:dangerous_query_prompted', {
        kind: decision.dangerInfo.kind ?? 'unknown',
        severity: decision.dangerInfo.severity ?? 'high',
        engine: engineId,
        source: options.source,
      });
      return new Promise<QueryExecutionOutcome>((resolve) => {
        dangerousOutcomeRef.current = resolve;
      });
    }

    return await doExecuteQuery(textToExecute, options);
  }, [engineId, doExecuteQuery, takeDangerousWaiter, track]);

  return {
    dangerInfo,
    dangerousDialogOpen,
    handleDangerousConfirm,
    handleDangerousCancel,
    runQueryText,
  };
}
